import { useMemo, useState } from 'react'
import {
  Check,
  Pencil,
  PlusIcon,
  RedoDotIcon,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  useListHazardRiskReductionSteps,
  useDeleteAllHazardRiskReductionStepsForHazard,
  useToggleHazardIdentificationRiskAdequatelyReduced,
  useListRiskReductionMeasures,
  type HazardIdentification,
  type RiskAssessmentStep,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import { Skeleton } from '@/components/ui/skeleton'
import { useMachineryDirectiveEhsrList } from '../../hooks/useMachineryDirectiveEhsrList'
import { CreateHazardRiskReductionStepSheet } from './CreateHazardRiskReductionStepSheet'
import { EditHazardRiskReductionStepSheet } from './EditHazardRiskReductionStepSheet'
import { HazardRiskScore } from './HazardRiskScore'
import { RiskInFactorsGrid } from './RiskInFactorsGrid'
import { hasAtLeastOneCompleteStep } from './risk-sets/RiskAssessmentStepForm.schema'

type StepCardProps = {
  step: RiskAssessmentStep
  previousStep: RiskAssessmentStep | null
  onEditStep: (stepToEdit: RiskAssessmentStep) => void
}

function StepCard({ step, onEditStep }: StepCardProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const ehsrOptions = useMachineryDirectiveEhsrList()
  const ehsrLabelMap = useMemo(
    () => new Map(ehsrOptions.map((opt) => [opt.id, opt.selectedLabel])),
    [ehsrOptions]
  )
  const rrm = step.riskReductionMeasure
  const ehsrIds = rrm.ehsrClauseIds ?? []
  const hasEhsrClauses = ehsrIds.length > 0
  const confirmationPending = step.confirmationPending === true

  return (
    <Card className='grow space-y-4 rounded-lg border bg-muted/20'>
      <CardHeader>
        <div className='flex flex-row items-start justify-between gap-3'>
          <HazardRiskScore score={step.riskOutScore ?? 0} />

          <div className='min-w-0 flex-1 space-y-2'>
            <div className='flex flex-wrap items-center gap-2'>
              <CardTitle className='text-base'>
                {rrm.name} {rrm.type ? ` (${rrm.type})` : ''}
              </CardTitle>
            </div>

            <CardDescription>
              {rrm.description ? ` — ${rrm.description}` : ''}{' '}
            </CardDescription>
          </div>

          <div className='flex shrink-0 flex-col items-end gap-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => {
                onEditStep(step)
              }}
            >
              <Pencil className='mr-1.5 size-3.5' aria-hidden />
              {t('hazardsPage.hazardDetail.riskFlow.editStep', 'Edit')}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {confirmationPending && (
          <Alert variant='warning' className='mb-4 text-left'>
            <AlertTitle>
              {t(
                'hazardsPage.hazardDetail.riskFlow.confirmationRequiredBadge',
                'Confirmation required'
              )}
            </AlertTitle>
            <div className='col-span-2 w-full justify-between xl:flex'>
              <AlertDescription>
                {t(
                  'hazardsPage.hazardDetail.riskFlow.confirmationRequiredBadgeTooltip',
                  'A previous step in this chain was edited. Please check that this risk reduction measure is still correct—for example the residual risk (risk out)—then save to confirm.'
                )}
              </AlertDescription>
              <AlertAction>
                <Button
                  type='button'
                  size='xs'
                  variant='warning'
                  onClick={() => {
                    onEditStep(step)
                  }}
                >
                  {t(
                    'hazardsPage.hazardDetail.riskFlow.confirmationRequiredAlertAction',
                    'Review & confirm'
                  )}
                </Button>
              </AlertAction>
            </div>
          </Alert>
        )}
        <RiskInFactorsGrid
          injury={step.riskOutInjury}
          frequency={step.riskOutFrequency}
          likelihood={step.riskOutLikelihood}
          avoidance={step.riskOutAvoidance}
        />
        {hasEhsrClauses && (
          <Item variant='outline' size='xs' className='mt-4'>
            <ItemMedia variant='icon'>
              <ShieldCheck />
            </ItemMedia>
            <ItemContent>
              <ItemTitle>{t('hazardsPage.hazardDetail.ehsr')}</ItemTitle>
              <ItemDescription>
                <div className='flex flex-wrap gap-1 pt-0.5'>
                  {ehsrIds.map((clauseId) => (
                    <Badge
                      key={clauseId}
                      variant='secondary'
                      className='text-xs'
                    >
                      {ehsrLabelMap.get(clauseId) ?? clauseId}
                    </Badge>
                  ))}
                </div>
              </ItemDescription>
            </ItemContent>
          </Item>
        )}
      </CardContent>
    </Card>
  )
}

export type HazardRiskAssessmentFlowProps = {
  hazardId: string
  complianceId: string
  hazard: HazardIdentification
}

export function HazardRiskAssessmentFlow({
  hazardId,
  complianceId,
  hazard,
}: HazardRiskAssessmentFlowProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { data: steps = [], isLoading } =
    useListHazardRiskReductionSteps(hazardId)
  const { data: riskReductionMeasures = [] } =
    useListRiskReductionMeasures(complianceId)
  const deleteAllStepsMutation = useDeleteAllHazardRiskReductionStepsForHazard(
    hazardId,
    complianceId
  )
  const toggleRiskAdequatelyReducedMutation =
    useToggleHazardIdentificationRiskAdequatelyReduced(complianceId)
  const [isAddStepSheetOpen, setIsAddStepSheetOpen] = useState(false)
  const [isStartOverDialogOpen, setIsStartOverDialogOpen] = useState(false)
  const [editingStep, setEditingStep] = useState<RiskAssessmentStep | null>(
    null
  )

  const handleOpenAddStepSheet = () => {
    setIsAddStepSheetOpen(true)
  }

  const previousStepForDefaults =
    steps.length > 0 ? steps[steps.length - 1] : null
  const defaultRiskOut = previousStepForDefaults
    ? {
        riskOutInjury: previousStepForDefaults.riskOutInjury ?? undefined,
        riskOutFrequency: previousStepForDefaults.riskOutFrequency ?? undefined,
        riskOutAvoidance: previousStepForDefaults.riskOutAvoidance ?? undefined,
        riskOutLikelihood:
          previousStepForDefaults.riskOutLikelihood ?? undefined,
      }
    : undefined

  const isRiskAdequatelyReduced =
    hazard.riskAdequatelyReducedAt != null &&
    hazard.riskAdequatelyReducedAt.trim() !== ''

  const handleRiskAdequatelyReducedChange = () => {
    toggleRiskAdequatelyReducedMutation.mutate(hazardId, {
      onSuccess: () => {
        toast.success(t('actions.saved'))
      },
      onError: (err) => {
        toast.error(err.message)
      },
    })
  }

  const handleConfirmStartOver = async () => {
    try {
      await deleteAllStepsMutation.mutateAsync()
      toast.success(t('hazardsPage.hazardDetail.riskFlow.stepsRemoved'))
      setIsStartOverDialogOpen(false)
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : t('hazardsPage.hazardDetail.riskFlow.startOverFailed')
      toast.error(message)
    }
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <Skeleton className='h-48 w-full max-w-xl' />
      </div>
    )
  }

  const hasSteps = steps.length > 0
  const canMarkRiskAdequatelyReduced = hasAtLeastOneCompleteStep(steps)
  const hasConfirmationPending = steps.some(
    (item) => item.confirmationPending === true
  )
  const confirmationRequiredHint = t(
    'hazardsPage.hazardDetail.riskFlow.confirmationRequiredHint',
    'Confirm or edit each step marked “Confirmation required” before adding measures or marking risk reduced.'
  )

  return (
    <>
      <Card>
        <CardHeader className='mb-2'>
          <CardTitle>{t('hazardsPage.hazardDetail.riskFlow.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='space-y-6 pt-2'>
            {!hasSteps && (
              <Empty className='min-h-[240px] rounded-lg border border-dashed'>
                <EmptyHeader>
                  <EmptyMedia variant='icon'>
                    <ShieldAlert className='size-10' />
                  </EmptyMedia>
                  <EmptyTitle>
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.noRiskReductionMeasuresTitle'
                    )}
                  </EmptyTitle>
                  <EmptyDescription>
                    {t(
                      riskReductionMeasures.length === 0
                        ? 'hazardsPage.hazardDetail.riskFlow.noRiskReductionMeasuresDescription'
                        : 'hazardsPage.hazardDetail.riskFlow.noRiskReductionMeasuresDescriptionWhenLibraryHasMeasures'
                    )}
                  </EmptyDescription>
                </EmptyHeader>
                {riskReductionMeasures.length > 0 && (
                  <EmptyContent>
                    <Button
                      onClick={handleOpenAddStepSheet}
                      variant='outline'
                      size='sm'
                    >
                      {t(
                        'hazardsPage.hazardDetail.riskFlow.addRiskReductionMeasure'
                      )}
                    </Button>
                  </EmptyContent>
                )}
              </Empty>
            )}

            {hasSteps && (
              <div className='space-y-4'>
                {steps.map((step, index) => (
                  <StepCard
                    key={step.id}
                    step={step}
                    previousStep={index > 0 ? steps[index - 1] : null}
                    onEditStep={setEditingStep}
                  />
                ))}

                {hasConfirmationPending && (
                  <Alert variant='warning'>
                    <AlertDescription>
                      {confirmationRequiredHint}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            <EditHazardRiskReductionStepSheet
              open={editingStep != null}
              onOpenChange={(open) => {
                if (!open) {
                  setEditingStep(null)
                }
              }}
              hazardId={hazardId}
              complianceId={complianceId}
              step={editingStep}
            />

            <CreateHazardRiskReductionStepSheet
              open={isAddStepSheetOpen}
              onOpenChange={setIsAddStepSheetOpen}
              hazardId={hazardId}
              complianceId={complianceId}
              defaultRiskOut={defaultRiskOut}
            />
          </div>
        </CardContent>
        <CardFooter className='mt-4 grid grid-cols-2 gap-2'>
          <div>
            {hasSteps && (
              <Button
                variant='outline'
                size='sm'
                onClick={() => setIsStartOverDialogOpen(true)}
                disabled={deleteAllStepsMutation.isPending}
              >
                <RedoDotIcon className='size-4' />
                {t('hazardsPage.hazardDetail.riskFlow.startOver')}
              </Button>
            )}
          </div>

          <div className='flex items-center justify-end gap-2'>
            {hasSteps && (
              <Button
                variant='outline'
                size='sm'
                onClick={handleOpenAddStepSheet}
                disabled={
                  !canMarkRiskAdequatelyReduced || hasConfirmationPending
                }
                title={
                  hasConfirmationPending ? confirmationRequiredHint : undefined
                }
              >
                <PlusIcon className='size-4' />
                {t('hazardsPage.hazardDetail.riskFlow.addRiskReductionMeasure')}
              </Button>
            )}
            <Button
              type='button'
              variant={isRiskAdequatelyReduced ? 'default' : 'outline'}
              size='sm'
              onClick={handleRiskAdequatelyReducedChange}
              disabled={
                toggleRiskAdequatelyReducedMutation.isPending ||
                !canMarkRiskAdequatelyReduced ||
                hasConfirmationPending
              }
              title={
                hasConfirmationPending ? confirmationRequiredHint : undefined
              }
              className={
                isRiskAdequatelyReduced
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-700'
                  : undefined
              }
            >
              <Check className='size-4' aria-hidden />
              {t('hazardsPage.hazardDetail.riskFlow.riskAdequatelyReduced')}
            </Button>
          </div>
        </CardFooter>
      </Card>
      <Dialog
        open={isStartOverDialogOpen}
        onOpenChange={setIsStartOverDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('hazardsPage.hazardDetail.riskFlow.startOverDialogTitle')}
            </DialogTitle>
          </DialogHeader>
          <p>{t('hazardsPage.hazardDetail.riskFlow.startOverDialogMessage')}</p>
          <DialogFooter className='border-t pt-4'>
            <Button
              variant='outline'
              onClick={() => setIsStartOverDialogOpen(false)}
              disabled={deleteAllStepsMutation.isPending}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              variant='destructive'
              onClick={handleConfirmStartOver}
              disabled={deleteAllStepsMutation.isPending}
            >
              {t('hazardsPage.hazardDetail.riskFlow.startOver')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
